import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Checkbox from "@mui/material/Checkbox";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  deleteDoc,
} from "firebase/firestore";
import { DATABASE } from "../../../Config/firebase";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import TopHeader from "../../../Components/TopHeader/TopHeader";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { dolorFormatAmount } from "../../../Utils/Services";
import axios from "axios";
import FUNCTIONS_URLS from '../../../Config/functions';


const YieldFinalSubmission = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [monthlyRecords, setMonthlyRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [processingData, setProcessingData] = useState(null);
  const [submittedFinalRecords, setSubmittedFinalRecords] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showAllDetailsDialog, setShowAllDetailsDialog] = useState(false);
  const [showParticipantDetailsDialog, setShowParticipantDetailsDialog] = useState(false);
  const [selectedParticipantDetails, setSelectedParticipantDetails] = useState(null);
  const [selectedFinalRecord, setSelectedFinalRecord] = useState(null);
  const [expandedHistoryRows, setExpandedHistoryRows] = useState(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [historySortBy, setHistorySortBy] = useState("date_desc");
  const [isNotify, setIsNotify] = useState([]); // Selected participants for PDF email
  const [dialogEnrichedParticipants, setDialogEnrichedParticipants] = useState(null);
  const [isEnrichingDialog, setIsEnrichingDialog] = useState(false);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch all products and sort in memory to avoid index requirement
        const snapshot = await getDocs(collection(DATABASE, "yields_products"));
        const data = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const aTime = a.created_at?.toMillis() || 0;
            const bTime = b.created_at?.toMillis() || 0;
            return bTime - aTime;
          });
        setProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load yield products");
      }
    };

    fetchProducts();
    loadAllSubmittedFinalRecords(); // Load all records on mount
  }, []);

  // Formik validation schema
  const validationSchema = Yup.object({
    product_id: Yup.string().required("Product is required"),
    transaction_date: Yup.mixed()
      .required("Transaction date is required")
      .test("is-valid-date", "Invalid date/time", (value) => dayjs(value).isValid()),
  });

  // Load submitted final records for a specific product (returns records, doesn't set state)
  const loadSubmittedFinalRecordsForProduct = async (productId) => {
    if (!productId) {
      console.log('⚠️ loadSubmittedFinalRecordsForProduct: productId is empty');
      return [];
    }

    try {
      console.log(`🔍 loadSubmittedFinalRecordsForProduct: Searching for product_id="${productId}"`);
      
      // Query without orderBy to avoid composite index requirement
      const finalQuery = query(
        collection(DATABASE, "yields_final"),
        where("product_id", "==", productId)
      );

      const finalSnapshot = await getDocs(finalQuery);
      console.log(`📊 Found ${finalSnapshot.docs.length} final submission(s) for product_id="${productId}"`);
      
      const records = finalSnapshot.docs
        .map((doc) => {
          const data = doc.data();
          console.log(`   Record: id=${doc.id}, product_id="${data.product_id}", product_name="${data.product_name || 'N/A'}"`);
          return {
            id: doc.id,
            ...data,
          };
        })
        .sort((a, b) => {
          // Sort in memory by transaction_date descending
          const aTime = a.transaction_date?.toMillis() || 0;
          const bTime = b.transaction_date?.toMillis() || 0;
          return bTime - aTime;
        });

      return records;
    } catch (error) {
      console.error("Error loading submitted final records:", error);
      return [];
    }
  };

  // Load ALL submitted final records (for history table)
  const loadAllSubmittedFinalRecords = async () => {
    setLoadingHistory(true);
    try {
      const finalSnapshot = await getDocs(collection(DATABASE, "yields_final"));
      const records = finalSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) => {
          // Sort in memory by transaction_date descending
          const aTime = a.transaction_date?.toMillis() || 0;
          const bTime = b.transaction_date?.toMillis() || 0;
          return bTime - aTime;
        });

      setSubmittedFinalRecords(records);
    } catch (error) {
      console.error("Error loading all submitted final records:", error);
      toast.error("Failed to load submitted final records");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Enrich participants with user_type from users collection (for historical records)
  const enrichParticipantsWithUserType = async (parts) => {
    const needsEnrichment = parts.filter(
      (p) => p.user_type === undefined || p.user_type === null
    );
    if (needsEnrichment.length === 0) return parts;

    const userTypeMap = {};
    await Promise.all(
      needsEnrichment.map(async (p) => {
        try {
          const userSnap = await getDoc(doc(DATABASE, "users", p.user_id));
          userTypeMap[p.user_id] = userSnap.exists()
            ? userSnap.data().user_type
            : 0;
        } catch {
          userTypeMap[p.user_id] = 0;
        }
      })
    );

    return parts.map((p) => ({
      ...p,
      user_type:
        p.user_type !== undefined && p.user_type !== null
          ? p.user_type
          : (userTypeMap[p.user_id] ?? 0),
    }));
  };

  // Open Details dialog — enriches participants with user_type if missing
  const openDetailsDialog = async (record) => {
    setSelectedFinalRecord(record);
    setDialogEnrichedParticipants(null);
    setShowAllDetailsDialog(true);
    setIsEnrichingDialog(true);
    try {
      const enriched = await enrichParticipantsWithUserType(
        record.participants || []
      );
      setDialogEnrichedParticipants(enriched);
    } finally {
      setIsEnrichingDialog(false);
    }
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      product_id: "",
      transaction_date: dayjs(),
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleCalculateFinal(values);
    },
  });

  // Load submitted records when product is selected (after formik initialization)
  // Note: This is only for calculating - history table loads all records separately
  useEffect(() => {
    if (formik.values.product_id && products.length > 0) {
      const product = products.find((p) => p.id === formik.values.product_id);
      if (product) {
        setSelectedProduct(product);
        // Only load for this product when calculating (for warning check)
        // History table loads all records separately
      } else {
        // Product ID exists but product not found - clear selection
        console.warn("Product not found for ID:", formik.values.product_id);
        setSelectedProduct(null);
      }
    } else if (!formik.values.product_id) {
      setSelectedProduct(null);
      // Don't reset submittedFinalRecords - history table should keep showing all records
    }
  }, [formik.values.product_id, products]);

  // Calculate final yield for all participants
  const handleCalculateFinal = async (values) => {
    setIsLoading(true);
    try {
      // Find selected product
      const product = products.find((p) => p.id === values.product_id);
      if (!product) {
        toast.error("Product not found");
        setIsLoading(false);
        return;
      }

      setSelectedProduct(product);

      // Check if already submitted (for warning only - doesn't affect history table)
      console.log(`🔍 Checking final submissions for product: ${product.product_name} (product_id: ${product.product_id})`);
      const existingRecords = await loadSubmittedFinalRecordsForProduct(product.product_id);
      console.log(`📋 Found ${existingRecords.length} existing final submission(s) for product_id: ${product.product_id}`);
      if (existingRecords.length > 0) {
        console.log(`   Existing records:`, existingRecords.map(r => ({
          id: r.id,
          product_id: r.product_id,
          product_name: r.product_name,
          transaction_date: r.transaction_date?.toDate?.() || r.transaction_date
        })));
      }

      // Fetch all monthly records for this product (without orderBy to avoid composite index)
      const monthlyQuery = query(
        collection(DATABASE, "yields_monthly"),
        where("product_id", "==", product.product_id)
      );

      const monthlySnapshot = await getDocs(monthlyQuery);
      const records = monthlySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) => {
          // Sort in memory by year then month ascending
          if (a.year !== b.year) return a.year - b.year;
          return a.month - b.month;
        });

      setMonthlyRecords(records);

      // Aggregate yield by user
      const userYieldMap = {};

      records.forEach((record) => {
        record.participants.forEach((participant) => {
          if (!userYieldMap[participant.user_id]) {
            userYieldMap[participant.user_id] = {
              user_id: participant.user_id,
              user_name: participant.user_name,
              user_type: participant.user_type, // 0 = Individual, 1 = Corporate
              total_gross_yield: 0,
              total_commission: 0,
              total_net_yield: 0,
              months_participated: 0,
              monthlyDetails: [], // Store monthly breakdown with dailyDetails
            };
          }
          // Handle both old format (yield_amount) and new format (net_yield_amount)
          const grossYield = participant.gross_yield_amount || 0;
          const commission = participant.fiddux_commission || 0;
          const netYield = participant.net_yield_amount || participant.yield_amount || 0;
          
          userYieldMap[participant.user_id].total_gross_yield += grossYield;
          userYieldMap[participant.user_id].total_commission += commission;
          userYieldMap[participant.user_id].total_net_yield += netYield;
          userYieldMap[participant.user_id].months_participated += 1;
          
          // Collect monthly details with daily breakdown
          userYieldMap[participant.user_id].monthlyDetails.push({
            month: record.month,
            year: record.year,
            gross_yield: grossYield,
            commission: commission,
            net_yield: netYield,
            days_participated: participant.days_participated || 0,
            monthly_percentage: participant.monthly_percentage || participant.custom_percentage || 0,
            dailyDetails: participant.dailyDetails || [], // Preserve day-by-day breakdown
          });
        });
      });

      // Convert to array and fetch current net_balance
      const participantsList = [];
      for (const userId in userYieldMap) {
        const userData = userYieldMap[userId];

        // Fetch user's current net_balance
        const userDoc = await getDocs(
          query(collection(DATABASE, "users"), where("__name__", "==", userId))
        );

        let currentNetBalance = 0;
        if (!userDoc.empty) {
          currentNetBalance = parseFloat(userDoc.docs[0].data().net_balance || 0);
        }

        participantsList.push({
          ...userData,
          current_net_balance: currentNetBalance,
          new_net_balance: currentNetBalance + userData.total_net_yield,
        });
      }

      // Enrich with user_type from users collection (monthly records may not have it)
      const enrichedList = await enrichParticipantsWithUserType(participantsList);
      setParticipants(enrichedList);
      setIsNotify([]); // Reset email selection when calculating new final
      setIsLoading(false);

      // Check if already submitted - show strong warning
      if (existingRecords.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Product Already Finalized",
          html: `
            <p><strong>This product already has ${existingRecords.length} final submission(s).</strong></p>
            <p>You can calculate again for verification purposes, but <strong>DO NOT submit again</strong> unless you delete the existing submission first.</p>
            <p style="margin-top: 15px; color: #d32f2f;"><strong>⚠️ Submitting again will create duplicate transactions and cause balance errors.</strong></p>
          `,
          confirmButtonText: "I Understand",
          confirmButtonColor: "#ff9800",
          showCancelButton: false,
        });
      }

      if (participantsList.length === 0) {
        toast.info("No participants found for this product");
      }
    } catch (error) {
      console.error("Error calculating final yield:", error);
      toast.error("Failed to calculate final yield");
      setIsLoading(false);
    }
  };

  // Handle submit final yield
  const handleSubmitFinal = () => {
    if (participants.length === 0) {
      toast.warning("No participants to process");
      return;
    }

    if (!selectedProduct) {
      toast.error("No product selected");
      return;
    }

    // STRICT CHECK: Block submission if THIS SPECIFIC PRODUCT already has final submission
    // Filter submittedFinalRecords by product_id to check only this product
    const productFinalRecords = submittedFinalRecords.filter(
      (record) => record.product_id === selectedProduct.product_id
    );

    console.log(`🔍 handleSubmitFinal: Checking for product_id="${selectedProduct.product_id}"`);
    console.log(`📊 Total final records: ${submittedFinalRecords.length}, For this product: ${productFinalRecords.length}`);

    if (productFinalRecords.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Product Already Finalized",
        html: `
          <p><strong>This product already has ${productFinalRecords.length} final submission(s).</strong></p>
          <p>Submitting again will create <strong>DUPLICATE transactions</strong> and cause balance errors.</p>
          <p style="margin-top: 15px;">If you need to recalculate, please:</p>
          <ol style="text-align: left; margin-left: 20px;">
            <li>Delete the existing final submission first</li>
            <li>Then submit again with corrected data</li>
          </ol>
        `,
        confirmButtonText: "I Understand",
        confirmButtonColor: "#d32f2f",
        showCancelButton: false,
      });
      return; // Block submission
    }

    setProcessingData({
      product_id: selectedProduct.product_id,
      product_name: selectedProduct.product_name,
      transaction_date: formik.values.transaction_date,
      participants,
      isNotify, // Include selected participants for PDF
    });
    setShowConfirmDialog(true);
  };

  // Confirm and process final yield
  const confirmProcessFinal = async () => {
    setShowConfirmDialog(false);
    setIsProcessing(true);

    try {
      const { product_id, product_name, transaction_date, participants } = processingData;

      // Check if already submitted for this exact date (prevent duplicates)
      const existingRecords = await loadSubmittedFinalRecordsForProduct(product_id);
      const sameDate = existingRecords.find(record => {
        const recordDate = dayjs(record.transaction_date?.toDate()).format("YYYY-MM-DD");
        const newDate = dayjs(transaction_date).format("YYYY-MM-DD");
        return recordDate === newDate;
      });
      
      if (sameDate) {
        toast.error(`⚠️ A final submission already exists for this product on ${dayjs(transaction_date).format("MMM DD, YYYY")}. Please delete the existing one first or choose a different date.`);
        setIsProcessing(false);
        return;
      }

      // Usa a data E hora escolhidas no DateTimePicker (fuso local), preservando o horário.
      // Antes usava new Date("YYYY-MM-DD") => meia-noite UTC (01:00 BST), o que fazia o
      // fechamento aparecer fora de ordem em relação ao Monthly Performance.
      const transactionTimestamp = Timestamp.fromDate(dayjs(transaction_date).toDate());

      // Process each participant
      for (const participant of participants) {
        // Generate unique IDs for ledger and transaction
        const ledgerDocId = `${participant.user_id}_${dayjs(transactionTimestamp.toDate()).format("YYYYMMDDHHmmss")}`;
        const transactionDocId = `${participant.user_id}_${dayjs(transactionTimestamp.toDate()).format("YYYYMMDDHHmmss")}`;

        // 1. Create ledger entry (transection_type: 4 for Yield Performance)
        const ledgerData = {
          user_id: participant.user_id,
          transection_type: 4, // Yield Performance (type 4)
          profit_loss_balance: participant.total_net_yield, // NET amount (after commission)
          profit_loss_percent: 0, // Yield doesn't use percentage like Performance
          date: transactionTimestamp,
          created_at: transactionTimestamp,
          request_date: transactionTimestamp, // When user requested (transaction date)
          conclusion_date: transactionTimestamp, // When it was concluded
          doc_id: ledgerDocId,
          new_txn_amount: participant.total_net_yield,
          previous_balance: participant.current_net_balance,
          current_balance: participant.new_net_balance,
        };

        await addDoc(collection(DATABASE, "ledgers"), ledgerData);

        // 2. Create transaction record (request_type: 3 = YIELD_PERFORMANCE, request_status: 5 = PAID)
        const transactionData = {
          user_id: participant.user_id,
          user_name: participant.user_name,
          request_type: 3, // YIELD_PERFORMANCE (type 3)
          request_status: 5, // PAID (status 5)
          date: transactionTimestamp,
          created_at: transactionTimestamp,
          request_approval_time: transactionTimestamp,
          
          // Amounts
          total_amount: participant.total_net_yield, // NET amount (what user receives)
          profit_loss: participant.total_net_yield, // Same as total_amount for yield
          profit_loss_percent: 0, // Yield doesn't use percentage
          
          // Additional yield-specific fields (for reference/reporting)
          gross_amount: participant.total_gross_yield, // Store gross for admin reference
          commission_amount: participant.total_commission, // Store commission for admin reference
          yield_product_id: product_id,
          yield_product_name: product_name,
          months_participated: participant.months_participated,
          
          // Standard transaction fields
          admin_commission: participant.total_commission, // Match with Performance structure
          remaining_balance: 0,
          transection_fee: 0,
          withdraw_amount: 0,
          otp: "0",
          otp_verify: true,
          notes: `Yield Performance - ${product_name}`,
          payment_receipt: "",
          
          // IDs
          txn_id: Number(dayjs(transactionTimestamp.toDate()).format("YYYYMMDDHHmmss")),
          doc_id: transactionDocId,
          transaction_id: transactionDocId,
        };

        await addDoc(collection(DATABASE, "transection_requests"), transactionData);

        // 3. Update user's net_balance
        const userRef = doc(DATABASE, "users", participant.user_id);
        await updateDoc(userRef, {
          net_balance: participant.new_net_balance,
          yield_active: false, // Deactivate yield participation (product end — not client toggle)
          yield_active_product_id: "",
          yield_deactivation_source: "product_final",
        });

        // 4. Update user's yield_total_earned (store NET amount)
        await updateDoc(userRef, {
          [`yield_total_earned.${product_id}`]: participant.total_net_yield,
        });
      }

      // 4. Create final record in yields_final
      const indParts = participants.filter((p) => p.user_type !== 1);
      const corpParts = participants.filter((p) => p.user_type === 1);
      const finalRecord = {
        product_id,
        product_name,
        total_participants: participants.length,
        individual_count: indParts.length,
        corporate_count: corpParts.length,
        monthly_records_count: monthlyRecords.length,
        total_gross_yield: participants.reduce((sum, p) => sum + p.total_gross_yield, 0),
        total_commission: participants.reduce((sum, p) => sum + p.total_commission, 0),
        total_net_yield: participants.reduce((sum, p) => sum + p.total_net_yield, 0),
        individual_gross_yield: indParts.reduce((sum, p) => sum + p.total_gross_yield, 0),
        individual_commission: indParts.reduce((sum, p) => sum + p.total_commission, 0),
        individual_net_yield: indParts.reduce((sum, p) => sum + p.total_net_yield, 0),
        corporate_gross_yield: corpParts.reduce((sum, p) => sum + p.total_gross_yield, 0),
        corporate_commission: corpParts.reduce((sum, p) => sum + p.total_commission, 0),
        corporate_net_yield: corpParts.reduce((sum, p) => sum + p.total_net_yield, 0),
        transaction_date: transactionTimestamp,
        participants: participants.map((p) => ({
          user_id: p.user_id,
          user_name: p.user_name,
          user_type: p.user_type, // 0 = Individual, 1 = Corporate
          gross_yield: p.total_gross_yield,
          total_commission: p.total_commission,
          net_yield: p.total_net_yield,
          months_participated: p.months_participated,
          previous_balance: p.current_net_balance,
          new_balance: p.new_net_balance,
          monthlyDetails: p.monthlyDetails || [], // Save monthly breakdown with dailyDetails
        })),
        processed_at: Timestamp.now(),
        processed_by: JSON.parse(localStorage.getItem("fiddux_info"))?.email || "admin",
      };

      await addDoc(collection(DATABASE, "yields_final"), finalRecord);

      toast.success(
        `Final yield submitted successfully for ${participants.length} participants!`
      );

      // Small delay to ensure Firestore has finished writing
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload all submitted final records to show the new submission
      await loadAllSubmittedFinalRecords();
      
      console.log("✅ Reloaded all final records after submission");

      // Send PDF emails to selected participants only
      const selectedParticipants = processingData?.isNotify || [];
      if (selectedParticipants.length > 0) {
        try {
          console.log('📧 Sending Yield Final PDF generation request for', selectedParticipants.length, 'users...');
          
          // Filter participants to only those selected
          const matchingEntries = participants.filter(p => selectedParticipants.includes(p.user_id));
          
          const pdfResponse = await axios.post(
            FUNCTIONS_URLS.SendPdfYieldFinalPerformance,
            {
              isNotify: selectedParticipants,
              matchingEntries,
              selectedProduct: {
                product_id: product_id,
                product_name: product_name,
                start_date: selectedProduct?.start_date,
                end_date: selectedProduct?.end_date,
                yield_period: selectedProduct?.yield_period || `${selectedProduct?.period_months || selectedProduct?.months || 1} ${(selectedProduct?.period_months || selectedProduct?.months || 1) === 1 ? 'month' : 'months'}`,
              },
            },
            {
              headers: { 'Content-Type': 'application/json' },
              timeout: 300000 // 5 minutes timeout
            }
          );
          
          console.log('✅ Yield Final PDF generation completed:', pdfResponse.data);
          toast.success(`PDF emails sent to ${selectedParticipants.length} participant(s)`);
        } catch (error) {
          console.error('❌ Error sending Yield Final PDF emails:', error);
          toast.warning('Final yield submitted, but PDF emails failed to send. You can send them manually later.');
        }
      } else {
        console.log('📧 No participants selected for PDF emails');
      }

      // Reset form and state (but keep product_id and selectedProduct so history table remains visible)
      const currentProductId = formik.values.product_id;
      formik.resetForm({
        values: {
          product_id: currentProductId, // Keep product selected
          transaction_date: dayjs(),
        },
      });
      setParticipants([]);
      setMonthlyRecords([]);
      // Keep selectedProduct so the history table remains visible
      // selectedProduct is already set and will remain set
      console.log("✅ Final yield submitted. selectedProduct:", selectedProduct?.product_name);
      setIsProcessing(false);
    } catch (error) {
      console.error("Error processing final yield:", error);
      toast.error("Failed to process final yield");
      setIsProcessing(false);
    }
  };

  // Handle delete record
  const handleDeleteRecord = (record) => {
    setRecordToDelete(record);
    setShowDeleteDialog(true);
  };

  // Handle send PDF email to individual participant
  const handleSendPDFMail = async (participant, record) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Would you like to send the Yield Final Performance report to ${participant.user_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#46adf2",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        // Prepare data for PDF generation
        const matchingEntries = [participant];
        const isNotify = [participant.user_id];
        
        // Get product details from record
        const selectedProduct = {
          product_id: record.product_id,
          product_name: record.product_name,
          start_date: null, // Not stored in final record, but not critical for PDF
          end_date: null, // Not stored in final record, but not critical for PDF
        };

        const response = await axios.post(
          FUNCTIONS_URLS.SendPdfYieldFinalPerformance,
          {
            isNotify,
            matchingEntries,
            selectedProduct,
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 300000 // 5 minutes timeout
          }
        );

        console.log('✅ Yield Final PDF generation completed:', response.data);
        toast.success(`You've successfully sent the Yield Final Performance PDF to ${participant.user_name}`);
        
        // Reload history to refresh any status indicators
        await loadAllSubmittedFinalRecords();
      } catch (error) {
        console.error('❌ Error sending Yield Final PDF:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
        toast.error(`Failed to send PDF: ${errorMessage}`);
      }
    }
  };

  // Confirm delete record
  const confirmDeleteRecord = async () => {
    if (!recordToDelete) return;
    
    setIsDeleting(true);
    try {
      // CRITICAL: Revert all transactions and balance updates
      console.log("🗑️ Starting deletion and reversal process for:", recordToDelete.id);
      
      // 1. Get all participants from the record
      const participants = recordToDelete.participants || [];
      
      for (const participant of participants) {
        console.log(`  ↩️ Reverting for user: ${participant.user_name} (${participant.user_id})`);
        
        // 2. Revert user balance (subtract the net yield)
        const userRef = doc(DATABASE, "users", participant.user_id);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const currentBalance = parseFloat(userDoc.data().net_balance || 0);
          const newBalance = currentBalance - (participant.net_yield || 0);
          
          console.log(`    Balance: ${currentBalance} -> ${newBalance} (subtracted ${participant.net_yield})`);
          
          await updateDoc(userRef, {
            net_balance: newBalance,
            yield_active: true,
            yield_active_product_id: recordToDelete.product_id,
            yield_deactivation_source: "product_final_rollback",
          });
        }
        
        // 3. Delete ledger entries for this transaction
        const transactionDate = dayjs(recordToDelete.transaction_date?.toDate());
        const ledgerId = `${participant.user_id}_${transactionDate.format("YYYYMMDDHHmmss")}`;
        
        try {
          const ledgerQuery = query(
            collection(DATABASE, "ledgers"),
            where("user_id", "==", participant.user_id),
            where("doc_id", "==", ledgerId)
          );
          const ledgerSnapshot = await getDocs(ledgerQuery);
          
          for (const ledgerDoc of ledgerSnapshot.docs) {
            await deleteDoc(doc(DATABASE, "ledgers", ledgerDoc.id));
            console.log(`    ✓ Deleted ledger: ${ledgerDoc.id}`);
          }
        } catch (error) {
          console.error(`    ⚠️ Error deleting ledger for ${participant.user_id}:`, error);
        }
        
        // 4. Delete transaction request
        const transactionId = `${participant.user_id}_${transactionDate.format("YYYYMMDDHHmmss")}`;
        
        try {
          const transactionQuery = query(
            collection(DATABASE, "transection_requests"),
            where("user_id", "==", participant.user_id),
            where("doc_id", "==", transactionId)
          );
          const transactionSnapshot = await getDocs(transactionQuery);
          
          for (const transactionDoc of transactionSnapshot.docs) {
            await deleteDoc(doc(DATABASE, "transection_requests", transactionDoc.id));
            console.log(`    ✓ Deleted transaction: ${transactionDoc.id}`);
          }
        } catch (error) {
          console.error(`    ⚠️ Error deleting transaction for ${participant.user_id}:`, error);
        }
        
        // 5. Remove yield_total_earned for this product
        try {
          await updateDoc(userRef, {
            [`yield_total_earned.${recordToDelete.product_id}`]: 0,
          });
          console.log(`    ✓ Reset yield_total_earned for product`);
        } catch (error) {
          console.error(`    ⚠️ Error resetting yield_total_earned:`, error);
        }
      }
      
      // 6. Finally, delete the yields_final record
      await deleteDoc(doc(DATABASE, "yields_final", recordToDelete.id));
      
      toast.success("Record deleted and all transactions reverted successfully!");
      console.log("✅ Deletion and reversal complete");
      
      setShowDeleteDialog(false);
      setRecordToDelete(null);
      
      // Small delay to ensure Firestore has finished all operations
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reload all history
      await loadAllSubmittedFinalRecords();
      
      console.log("✅ Reloaded all final records after deletion");
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record: " + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate totals
  const totalGrossYield = participants.reduce((sum, p) => sum + p.total_gross_yield, 0);
  const totalCommission = participants.reduce((sum, p) => sum + p.total_commission, 0);
  const totalNetYield = participants.reduce((sum, p) => sum + p.total_net_yield, 0);

  // Split by account type
  const individualParticipants = participants.filter((p) => p.user_type !== 1);
  const corporateParticipants = participants.filter((p) => p.user_type === 1);
  const indGrossYield = individualParticipants.reduce((sum, p) => sum + p.total_gross_yield, 0);
  const indCommission = individualParticipants.reduce((sum, p) => sum + p.total_commission, 0);
  const indNetYield = individualParticipants.reduce((sum, p) => sum + p.total_net_yield, 0);
  const corpGrossYield = corporateParticipants.reduce((sum, p) => sum + p.total_gross_yield, 0);
  const corpCommission = corporateParticipants.reduce((sum, p) => sum + p.total_commission, 0);
  const corpNetYield = corporateParticipants.reduce((sum, p) => sum + p.total_net_yield, 0);

  // Process and filter history records
  const processedHistoryRecords = submittedFinalRecords
    .map((record) => {
      const participantNames = record.participants?.map(p => p.user_name || "N/A").join(", ") || "No participants";
      return {
        ...record,
        participantNames,
        processedDate: record.processed_at ? record.processed_at.toDate() : null,
      };
    })
    .filter((record) => {
      if (!historySearch) return true;
      const searchLower = historySearch.toLowerCase();
      return (
        record.product_name?.toLowerCase().includes(searchLower) ||
        record.participantNames?.toLowerCase().includes(searchLower) ||
        dayjs(record.transaction_date?.toDate()).format("MMMM YYYY").toLowerCase().includes(searchLower) ||
        record.processed_by?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      if (historySortBy === "date_desc") {
        const aTime = a.transaction_date?.toMillis() || 0;
        const bTime = b.transaction_date?.toMillis() || 0;
        return bTime - aTime;
      } else if (historySortBy === "date_asc") {
        const aTime = a.transaction_date?.toMillis() || 0;
        const bTime = b.transaction_date?.toMillis() || 0;
        return aTime - bTime;
      } else if (historySortBy === "name_asc") {
        const nameCompare = (a.product_name || "").localeCompare(b.product_name || "");
        if (nameCompare !== 0) return nameCompare;
        const aTime = a.transaction_date?.toMillis() || 0;
        const bTime = b.transaction_date?.toMillis() || 0;
        return bTime - aTime;
      }
      return 0;
    });

  return (
    <Box>
      <TopHeader title="Yield Final Submission" />

      <Box sx={{ 
        p: { xs: 2, md: 3 }, 
        mt: { xs: 10, md: 8 }, // Mais espaço em mobile para compensar TopHeader fixo
        bgcolor: "background.default", // Garante background escuro
      }}>
        {/* Header Information */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
            ⚠️ How Final Submission Works:
          </Typography>
          <Typography variant="body1" component="div" sx={{ fontSize: "1rem", lineHeight: 1.6 }}>
            <strong>Step 1:</strong> Select a Yield product that has completed its period and has monthly records processed.
            <br />
            <strong>Step 2:</strong> Set the transaction date (when users will receive their yield).
            <br />
            <strong>Step 3:</strong> Click "Calculate Final" to aggregate all monthly records and see totals.
            <br />
            <strong>Step 4:</strong> Review the summary showing Gross Yield, Commission, and Net Yield for all participants.
            <br />
            <strong>Step 5:</strong> Click "Submit Final Yield" to:
            <br />
            &nbsp;&nbsp;• Create transactions with <strong>NET amounts</strong> (what users receive)
            <br />
            &nbsp;&nbsp;• Update user balances with NET yield
            <br />
            &nbsp;&nbsp;• Deactivate yield participation for all users
            <br />
            &nbsp;&nbsp;• Send completion emails
            <br />
            <br />
            <strong style={{ color: '#d32f2f' }}>⚠️ WARNING:</strong> This action cannot be undone! 
            Make sure all calculations are correct before submitting.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Configuration Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Final Submission Configuration
                </Typography>

                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Yield Product"
                        name="product_id"
                        value={formik.values.product_id || ""}
                        onChange={(e) => {
                          formik.handleChange(e);
                          // Immediately set selectedProduct when product is selected
                          // Note: History table loads all records separately, so we don't reset it here
                          const productId = e.target.value;
                          if (productId) {
                            const product = products.find((p) => p.id === productId);
                            if (product) {
                              setSelectedProduct(product);
                              // Don't load records here - history table shows all records
                            }
                          } else {
                            setSelectedProduct(null);
                            // Don't reset submittedFinalRecords - history table should keep showing all records
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.product_id &&
                          Boolean(formik.errors.product_id)
                        }
                        helperText={
                          formik.touched.product_id && formik.errors.product_id
                        }
                        SelectProps={{ 
                          native: true,
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      >
                        <option value="" disabled>
                          Select Product
                        </option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.product_name} (
                            {product.start_date
                              ? dayjs(product.start_date.toDate()).format("MMM YYYY")
                              : "N/A"}{" "}
                            -{" "}
                            {product.end_date
                              ? dayjs(product.end_date.toDate()).format("MMM YYYY")
                              : "N/A"}
                            )
                          </option>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          label="Transaction Date/Time"
                          value={
                            formik.values.transaction_date
                              ? dayjs(formik.values.transaction_date)
                              : null
                          }
                          onChange={(newValue) => {
                            formik.setFieldValue("transaction_date", newValue);
                          }}
                          ampm={false}
                          format="DD/MM/YYYY HH:mm"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              onBlur: () =>
                                formik.setFieldTouched("transaction_date", true),
                              error:
                                formik.touched.transaction_date &&
                                Boolean(formik.errors.transaction_date),
                              helperText:
                                (formik.touched.transaction_date &&
                                  formik.errors.transaction_date) ||
                                "Choose the exact date AND time of the yield closing (default: now)",
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        sx={{
                          background:
                            "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #3adecc 0%, #2af598 100%)",
                          },
                        }}
                      >
                        {isLoading ? <CircularProgress size={24} /> : "Calculate Final"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>

                {participants.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Total Participants:</strong> {participants.length}
                        {individualParticipants.length > 0 && ` (${individualParticipants.length} Individual`}
                        {corporateParticipants.length > 0 && `, ${corporateParticipants.length} Corporate`}
                        {(individualParticipants.length > 0 || corporateParticipants.length > 0) && ")"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Monthly Records:</strong> {monthlyRecords.length}
                      </Typography>

                      {/* Individual totals */}
                      {individualParticipants.length > 0 && (
                        <Box sx={{ mt: 1.5, pl: 1, borderLeft: "3px solid #7eb8ff" }}>
                          <Typography variant="body2" sx={{ fontWeight: "bold", color: "#7eb8ff", mb: 0.5 }}>
                            Individual ({individualParticipants.length})
                          </Typography>
                          <Typography variant="body2">Gross: {dolorFormatAmount(indGrossYield)}</Typography>
                          <Typography variant="body2" sx={{ color: "#ff6b6b" }}>Commission: -{dolorFormatAmount(indCommission)}</Typography>
                          <Typography variant="body2" sx={{ color: "#00ba2d", fontWeight: "bold" }}>Net: {dolorFormatAmount(indNetYield)}</Typography>
                        </Box>
                      )}

                      {/* Corporate totals */}
                      {corporateParticipants.length > 0 && (
                        <Box sx={{ mt: 1, pl: 1, borderLeft: "3px solid #ffb74d" }}>
                          <Typography variant="body2" sx={{ fontWeight: "bold", color: "#ffb74d", mb: 0.5 }}>
                            Corporate ({corporateParticipants.length})
                          </Typography>
                          <Typography variant="body2">Gross: {dolorFormatAmount(corpGrossYield)}</Typography>
                          <Typography variant="body2" sx={{ color: "#ff6b6b" }}>Commission: -{dolorFormatAmount(corpCommission)}</Typography>
                          <Typography variant="body2" sx={{ color: "#00ba2d", fontWeight: "bold" }}>Net: {dolorFormatAmount(corpNetYield)}</Typography>
                        </Box>
                      )}

                      <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid rgba(0,0,0,0.12)" }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          Gross Yield: {dolorFormatAmount(totalGrossYield)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                          Commission (Fiddux): -{dolorFormatAmount(totalCommission)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#00ba2d", fontWeight: "bold" }}>
                          Net Yield (to Users): {dolorFormatAmount(totalNetYield)}
                        </Typography>
                      </Box>
                    </Alert>

                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        This will create transactions, update balances, and deactivate yield
                        participation for all users.
                      </Typography>
                    </Alert>

                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      onClick={handleSubmitFinal}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Submit Final Yield"
                      )}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Participants Table */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">
                    Final Participants ({participants.length})
                  </Typography>
                  {participants.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => {
                        openDetailsDialog({
                          participants: participants,
                          product_name: selectedProduct?.product_name,
                        });
                      }}
                    >
                      View All Details
                    </Button>
                  )}
                </Box>

                {participants.length > 0 && (
                  <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
                    The checkbox only selects who receives the final PDF report by email — it does
                    <strong> not</strong> choose who gets closed. <strong>All</strong> participants
                    listed below are processed on Submit. Leave it unchecked to close without sending
                    any email.
                  </Alert>
                )}

                {participants.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell padding="checkbox">
                            <Tooltip title="Send PDF report by email (optional). Does not affect who is closed.">
                              <Checkbox
                                indeterminate={isNotify.length > 0 && isNotify.length < participants.length}
                                checked={participants.length > 0 && isNotify.length === participants.length}
                                onChange={() => {
                                  if (isNotify.length === participants.length) {
                                    setIsNotify([]);
                                  } else {
                                    setIsNotify(participants.map(p => p.user_id));
                                  }
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>User</TableCell>
                          <TableCell align="center">Months</TableCell>
                          <TableCell align="right">Current Balance</TableCell>
                          <TableCell align="right">Gross Yield</TableCell>
                          <TableCell align="right">Commission</TableCell>
                          <TableCell align="right">Net Yield</TableCell>
                          <TableCell align="right">New Balance</TableCell>
                          <TableCell align="center">Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Helper to render a participant row */}
                        {[
                          { label: "Individual", color: "#7eb8ff", group: individualParticipants, gross: indGrossYield, comm: indCommission, net: indNetYield },
                          { label: "Corporate", color: "#ffb74d", group: corporateParticipants, gross: corpGrossYield, comm: corpCommission, net: corpNetYield },
                        ].map(({ label, color, group, gross, comm, net }) =>
                          group.length === 0 ? null : (
                            <React.Fragment key={label}>
                              {/* Section header */}
                              <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.04)" }}>
                                <TableCell colSpan={9} sx={{ py: 0.5, px: 2 }}>
                                  <Typography variant="caption" sx={{ fontWeight: "bold", color, letterSpacing: 0.5, textTransform: "uppercase" }}>
                                    {label} — {group.length} participant{group.length !== 1 ? "s" : ""}
                                  </Typography>
                                </TableCell>
                              </TableRow>

                              {/* Participant rows */}
                              {group.map((participant) => (
                                <TableRow key={participant.user_id}>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={isNotify.includes(participant.user_id)}
                                      onChange={() => {
                                        if (isNotify.includes(participant.user_id)) {
                                          setIsNotify(isNotify.filter(id => id !== participant.user_id));
                                        } else {
                                          setIsNotify([...isNotify, participant.user_id]);
                                        }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>{participant.user_name}</TableCell>
                                  <TableCell align="center">
                                    <Chip label={participant.months_participated} size="small" color="primary" />
                                  </TableCell>
                                  <TableCell align="right">
                                    {dolorFormatAmount(participant.current_net_balance)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ color: "#666" }}>
                                      {dolorFormatAmount(participant.total_gross_yield)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                                      -{dolorFormatAmount(participant.total_commission)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                                      +{dolorFormatAmount(participant.total_net_yield)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                      {dolorFormatAmount(participant.new_net_balance)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setSelectedParticipantDetails(participant);
                                        setShowParticipantDetailsDialog(true);
                                      }}
                                      color="primary"
                                      disabled={!participant.monthlyDetails || participant.monthlyDetails.length === 0}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}

                              {/* Subtotal row */}
                              <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.02)", borderTop: `2px solid ${color}22` }}>
                                <TableCell />
                                <TableCell colSpan={2}>
                                  <Typography variant="body2" sx={{ fontWeight: "bold", color }}>
                                    Subtotal {label}
                                  </Typography>
                                </TableCell>
                                <TableCell />
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: "#666", fontWeight: "bold" }}>
                                    {dolorFormatAmount(gross)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: "#ff6b6b", fontWeight: "bold" }}>
                                    -{dolorFormatAmount(comm)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                                    +{dolorFormatAmount(net)}
                                  </Typography>
                                </TableCell>
                                <TableCell colSpan={2} />
                              </TableRow>
                            </React.Fragment>
                          )
                        )}

                        {/* Grand total */}
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell />
                          <TableCell colSpan={2}>
                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                              TOTAL
                            </Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell align="right">
                            <Typography variant="body1" sx={{ color: "#666" }}>
                              {dolorFormatAmount(totalGrossYield)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" sx={{ color: "#ff6b6b" }}>
                              -{dolorFormatAmount(totalCommission)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                              +{dolorFormatAmount(totalNetYield)}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    Select a product and click "Calculate Final" to view participants
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Submitted Final Records History - Always Visible */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">
                    Final Submission History
                  </Typography>
                  <Chip
                    label={`${processedHistoryRecords.length} record(s)`}
                    color={processedHistoryRecords.length > 0 ? "success" : "default"}
                    size="small"
                  />
                </Box>

                {/* Filters */}
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                  <TextField
                    size="small"
                    placeholder="Search by product, user name, date, processed by..."
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    sx={{ minWidth: "300px", flex: 1 }}
                  />
                  <FormControl size="small" sx={{ minWidth: "200px" }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={historySortBy}
                      label="Sort By"
                      onChange={(e) => setHistorySortBy(e.target.value)}
                    >
                      <MenuItem value="date_desc">Latest First</MenuItem>
                      <MenuItem value="date_asc">Oldest First</MenuItem>
                      <MenuItem value="name_asc">Name (A-Z) + Date</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {loadingHistory ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : processedHistoryRecords.length === 0 ? (
                  <Alert severity="info">
                    {historySearch ? "No records match your search criteria" : "No final submissions yet"}
                  </Alert>
                ) : (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell width="50px"></TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell>Transaction Date</TableCell>
                            <TableCell>Processed By</TableCell>
                            <TableCell align="center">Participants</TableCell>
                            <TableCell align="center">Monthly Records</TableCell>
                            <TableCell align="right">Total Gross Yield</TableCell>
                            <TableCell align="right">Total Commission</TableCell>
                            <TableCell align="right">Total Net Yield</TableCell>
                            <TableCell align="center">Details</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {processedHistoryRecords.map((record) => {
                            const isExpanded = expandedHistoryRows.has(record.id);
                            return (
                              <React.Fragment key={record.id}>
                                <TableRow>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const newExpanded = new Set(expandedHistoryRows);
                                        if (isExpanded) {
                                          newExpanded.delete(record.id);
                                        } else {
                                          newExpanded.add(record.id);
                                        }
                                        setExpandedHistoryRows(newExpanded);
                                      }}
                                    >
                                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                      {record.product_name || "N/A"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {record.transaction_date
                                      ? dayjs(record.transaction_date.toDate()).format("MMM DD, YYYY HH:mm")
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {record.processed_by || "admin"}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={record.total_participants || record.participants?.length || 0}
                                      size="small"
                                      color="primary"
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={record.monthly_records_count || "N/A"}
                                      size="small"
                                      color="secondary"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ color: "#666" }}>
                                      {dolorFormatAmount(record.total_gross_yield || 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                                      {dolorFormatAmount(record.total_commission || 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                                      {dolorFormatAmount(record.total_net_yield || 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => openDetailsDialog(record)}
                                      color="primary"
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteRecord(record)}
                                      title="Delete this record"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={11} sx={{ py: 0, border: 0 }}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 2 }}>
                                        {/* ── Quick breakdown by type ── */}
                                        {(() => {
                                          const rParts = record.participants || [];
                                          const rInd = rParts.filter((p) => p.user_type !== 1);
                                          const rCorp = rParts.filter((p) => p.user_type === 1);
                                          const hasTypeData = rParts.some((p) => p.user_type !== undefined && p.user_type !== null);
                                          const rIndNet = rInd.reduce((s, p) => s + (p.net_yield || 0), 0);
                                          const rIndGross = rInd.reduce((s, p) => s + (p.gross_yield || 0), 0);
                                          const rIndComm = rInd.reduce((s, p) => s + (p.total_commission || 0), 0);
                                          const rCorpNet = rCorp.reduce((s, p) => s + (p.net_yield || 0), 0);
                                          const rCorpGross = rCorp.reduce((s, p) => s + (p.gross_yield || 0), 0);
                                          const rCorpComm = rCorp.reduce((s, p) => s + (p.total_commission || 0), 0);
                                          return hasTypeData && (rInd.length > 0 || rCorp.length > 0) ? (
                                            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                              {rInd.length > 0 && (
                                                <Grid item xs={12} sm={6} md={4}>
                                                  <Paper variant="outlined" sx={{ p: 1.5, borderColor: "#7eb8ff", borderWidth: 2, bgcolor: "#7eb8ff11", borderRadius: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: "bold", color: "#7eb8ff", display: "block", mb: 0.5 }}>
                                                      👤 Individual ({rInd.length})
                                                    </Typography>
                                                    <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px", alignItems: "center" }}>
                                                      <Typography variant="caption" color="text.secondary">Gross</Typography>
                                                      <Typography variant="caption" sx={{ textAlign: "right", color: "#666" }}>{dolorFormatAmount(rIndGross)}</Typography>
                                                      <Typography variant="caption" color="text.secondary">Commission</Typography>
                                                      <Typography variant="caption" sx={{ textAlign: "right", color: "#ff6b6b" }}>-{dolorFormatAmount(rIndComm)}</Typography>
                                                      <Typography variant="caption" sx={{ fontWeight: "bold" }}>Net</Typography>
                                                      <Typography variant="body2" sx={{ textAlign: "right", color: "#00ba2d", fontWeight: "bold" }}>{dolorFormatAmount(rIndNet)}</Typography>
                                                    </Box>
                                                  </Paper>
                                                </Grid>
                                              )}
                                              {rCorp.length > 0 && (
                                                <Grid item xs={12} sm={6} md={4}>
                                                  <Paper variant="outlined" sx={{ p: 1.5, borderColor: "#ffb74d", borderWidth: 2, bgcolor: "#ffb74d11", borderRadius: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: "bold", color: "#ffb74d", display: "block", mb: 0.5 }}>
                                                      🏢 Corporate ({rCorp.length})
                                                    </Typography>
                                                    <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px", alignItems: "center" }}>
                                                      <Typography variant="caption" color="text.secondary">Gross</Typography>
                                                      <Typography variant="caption" sx={{ textAlign: "right", color: "#666" }}>{dolorFormatAmount(rCorpGross)}</Typography>
                                                      <Typography variant="caption" color="text.secondary">Commission</Typography>
                                                      <Typography variant="caption" sx={{ textAlign: "right", color: "#ff6b6b" }}>-{dolorFormatAmount(rCorpComm)}</Typography>
                                                      <Typography variant="caption" sx={{ fontWeight: "bold" }}>Net</Typography>
                                                      <Typography variant="body2" sx={{ textAlign: "right", color: "#00ba2d", fontWeight: "bold" }}>{dolorFormatAmount(rCorpNet)}</Typography>
                                                    </Box>
                                                  </Paper>
                                                </Grid>
                                              )}
                                            </Grid>
                                          ) : !hasTypeData ? (
                                            <Alert severity="info" icon={false} sx={{ mb: 1.5, py: 0.5 }}>
                                              <Typography variant="caption">
                                                Type breakdown not available for this record — click <strong>Details</strong> (eye icon) to see Individual/Corporate split.
                                              </Typography>
                                            </Alert>
                                          ) : null;
                                        })()}

                                        {/* ── Participant rows ── */}
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                                          All Participants:
                                        </Typography>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>User Name</TableCell>
                                              <TableCell align="center">Type</TableCell>
                                              <TableCell align="center">Months</TableCell>
                                              <TableCell align="right">Gross Yield</TableCell>
                                              <TableCell align="right">Commission</TableCell>
                                              <TableCell align="right">Net Yield</TableCell>
                                              <TableCell align="center">Details</TableCell>
                                              <TableCell align="center">Send PDF</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {record.participants?.map((participant, idx) => (
                                              <TableRow key={idx}>
                                                <TableCell>{participant.user_name || "N/A"}</TableCell>
                                                <TableCell align="center">
                                                  {participant.user_type === 1 ? (
                                                    <Chip label="Corporate" size="small" sx={{ bgcolor: "#ffb74d22", color: "#e65100", fontSize: "0.65rem" }} />
                                                  ) : participant.user_type === 0 ? (
                                                    <Chip label="Individual" size="small" sx={{ bgcolor: "#7eb8ff22", color: "#1565c0", fontSize: "0.65rem" }} />
                                                  ) : (
                                                    <Chip label="—" size="small" variant="outlined" sx={{ fontSize: "0.65rem" }} />
                                                  )}
                                                </TableCell>
                                                <TableCell align="center">
                                                  <Chip
                                                    label={participant.months_participated || 0}
                                                    size="small"
                                                    color="primary"
                                                  />
                                                </TableCell>
                                                <TableCell align="right">
                                                  {dolorFormatAmount(participant.gross_yield || 0)}
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                                                    -{dolorFormatAmount(participant.total_commission || 0)}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                                                    {dolorFormatAmount(participant.net_yield || 0)}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                  {participant.monthlyDetails && participant.monthlyDetails.length > 0 ? (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => {
                                                        setSelectedParticipantDetails(participant);
                                                        setShowParticipantDetailsDialog(true);
                                                      }}
                                                      color="primary"
                                                    >
                                                      <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                  ) : (
                                                    <Chip label="N/A" size="small" variant="outlined" />
                                                  )}
                                                </TableCell>
                                                <TableCell align="center">
                                                  <Tooltip title="Send PDF">
                                                    <IconButton
                                                      size="small"
                                                      sx={{
                                                        background: "#4caf50",
                                                        width: "30px",
                                                        height: "30px",
                                                        borderRadius: "4px",
                                                        "&:hover": { background: "#388e3c" },
                                                      }}
                                                      onClick={() => handleSendPDFMail(participant, record)}
                                                    >
                                                      <CheckIcon sx={{ color: "#fff", fontSize: "18px" }} />
                                                    </IconButton>
                                                  </Tooltip>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>⚠️ Confirm Final Yield Submission</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to submit the final yield for:
          </Typography>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography>
              <strong>Product:</strong> {processingData?.product_name}
            </Typography>
            <Typography>
              <strong>Transaction Date/Time:</strong>{" "}
              {processingData?.transaction_date
                ? dayjs(processingData.transaction_date).format("DD/MM/YYYY HH:mm")
                : "-"}
            </Typography>
            <Typography>
              <strong>Participants:</strong> {processingData?.participants.length}
            </Typography>
            <Typography>
              <strong>Gross Yield:</strong>{" "}
              {dolorFormatAmount(
                processingData?.participants.reduce((sum, p) => sum + p.total_gross_yield, 0) || 0
              )}
            </Typography>
            <Typography>
              <strong>Commission (Fiddux):</strong>{" "}
              {dolorFormatAmount(
                processingData?.participants.reduce((sum, p) => sum + p.total_commission, 0) || 0
              )}
            </Typography>
            <Typography>
              <strong>Net Yield (to Users):</strong>{" "}
              {dolorFormatAmount(
                processingData?.participants.reduce((sum, p) => sum + p.total_net_yield, 0) || 0
              )}
            </Typography>
          </Box>
          <Alert severity="error">
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              THIS ACTION CANNOT BE UNDONE!
            </Typography>
            <Typography variant="body2">
              - Transactions will be created
              <br />
              - User balances will be updated
              <br />
              - Yield participation will be deactivated
              <br />- PDF emails will be sent to {processingData?.isNotify?.length === 0 ? "NO participants" : `${processingData?.isNotify?.length || 0} selected participant(s)`}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmProcessFinal} variant="contained" color="error">
            Confirm Final Submission
          </Button>
        </DialogActions>
      </Dialog>

      {/* All Participants Details Dialog */}
      <Dialog
        open={showAllDetailsDialog}
        onClose={() => {
          setShowAllDetailsDialog(false);
          setSelectedFinalRecord(null);
          setDialogEnrichedParticipants(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Complete Yield Calculation Details
          {selectedFinalRecord?.product_name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedFinalRecord.product_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {isEnrichingDialog ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
              <CircularProgress size={32} sx={{ mr: 2 }} />
              <Typography color="text.secondary">Loading breakdown…</Typography>
            </Box>
          ) : dialogEnrichedParticipants ? (() => {
            const allParts = dialogEnrichedParticipants;
            const dlgInd = allParts.filter((p) => p.user_type !== 1);
            const dlgCorp = allParts.filter((p) => p.user_type === 1);

            const dlgIndGross = dlgInd.reduce((s, p) => s + (p.total_gross_yield || p.gross_yield || 0), 0);
            const dlgIndComm  = dlgInd.reduce((s, p) => s + (p.total_commission || 0), 0);
            const dlgIndNet   = dlgInd.reduce((s, p) => s + (p.total_net_yield || p.net_yield || 0), 0);
            const dlgCorpGross = dlgCorp.reduce((s, p) => s + (p.total_gross_yield || p.gross_yield || 0), 0);
            const dlgCorpComm  = dlgCorp.reduce((s, p) => s + (p.total_commission || 0), 0);
            const dlgCorpNet   = dlgCorp.reduce((s, p) => s + (p.total_net_yield || p.net_yield || 0), 0);
            const dlgTotalGross = dlgIndGross + dlgCorpGross;
            const dlgTotalComm  = dlgIndComm + dlgCorpComm;
            const dlgTotalNet   = dlgIndNet + dlgCorpNet;

            const summaryColumns = [
              ...(dlgInd.length > 0 ? [{ label: "👤 Individual", count: dlgInd.length, color: "#7eb8ff", gross: dlgIndGross, comm: dlgIndComm, net: dlgIndNet }] : []),
              ...(dlgCorp.length > 0 ? [{ label: "🏢 Corporate", count: dlgCorp.length, color: "#ffb74d", gross: dlgCorpGross, comm: dlgCorpComm, net: dlgCorpNet }] : []),
              { label: "📊 Total", count: allParts.length, color: "#aaa", gross: dlgTotalGross, comm: dlgTotalComm, net: dlgTotalNet, isTotal: true },
            ];

            const renderParticipantTable = (group) => (
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="center">Months</TableCell>
                      <TableCell align="right">Gross Yield</TableCell>
                      <TableCell align="right">Commission</TableCell>
                      <TableCell align="right">Net Yield</TableCell>
                      <TableCell align="center">Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.map((participant, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{participant.user_name || "N/A"}</TableCell>
                        <TableCell align="center">
                          <Chip label={participant.months_participated || 0} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="right">
                          {dolorFormatAmount(participant.total_gross_yield || participant.gross_yield || 0)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                            -{dolorFormatAmount(participant.total_commission || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                            {dolorFormatAmount(participant.total_net_yield || participant.net_yield || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {participant.monthlyDetails && participant.monthlyDetails.length > 0 ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedParticipantDetails(participant);
                                setShowAllDetailsDialog(false);
                                setShowParticipantDetailsDialog(true);
                              }}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <Chip label="N/A" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );

            return (
              <>
                {/* ── 3-COLUMN SUMMARY CARD ─────────────────────────────── */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {summaryColumns.map((col) => (
                    <Grid item xs={12} sm={summaryColumns.length === 3 ? 4 : 6} key={col.label}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor: col.isTotal ? "divider" : col.color,
                          borderWidth: col.isTotal ? 1 : 2,
                          borderRadius: 2,
                          bgcolor: col.isTotal ? "background.paper" : `${col.color}11`,
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: col.isTotal ? "text.primary" : col.color }}>
                            {col.label}
                          </Typography>
                          <Chip label={`${col.count} user${col.count !== 1 ? "s" : ""}`} size="small" sx={{ bgcolor: col.isTotal ? "action.hover" : `${col.color}33`, color: col.isTotal ? "text.secondary" : col.color, fontWeight: "bold" }} />
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Total Gross</Typography>
                          <Typography variant="body2" sx={{ textAlign: "right", color: "#666", fontWeight: col.isTotal ? "bold" : "normal" }}>
                            {dolorFormatAmount(col.gross)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Commission</Typography>
                          <Typography variant="body2" sx={{ textAlign: "right", color: "#ff6b6b", fontWeight: col.isTotal ? "bold" : "normal" }}>
                            -{dolorFormatAmount(col.comm)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "bold" }}>Net Yield</Typography>
                          <Typography variant="body1" sx={{ textAlign: "right", color: "#00ba2d", fontWeight: "bold" }}>
                            {dolorFormatAmount(col.net)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* ── INDIVIDUAL PARTICIPANT LIST ───────────────────────── */}
                {dlgInd.length > 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#7eb8ff", mb: 1, mt: 1 }}>
                      👤 Individual Clients — {dlgInd.length} participant{dlgInd.length !== 1 ? "s" : ""}
                    </Typography>
                    {renderParticipantTable(dlgInd)}
                  </>
                )}

                {/* ── CORPORATE PARTICIPANT LIST ────────────────────────── */}
                {dlgCorp.length > 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#ffb74d", mb: 1, mt: dlgInd.length > 0 ? 2 : 1 }}>
                      🏢 Corporate Clients — {dlgCorp.length} participant{dlgCorp.length !== 1 ? "s" : ""}
                    </Typography>
                    {renderParticipantTable(dlgCorp)}
                  </>
                )}
              </>
            );
          })() : (
            <Alert severity="info">No details available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAllDetailsDialog(false);
              setSelectedFinalRecord(null);
              setDialogEnrichedParticipants(null);
            }}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Participant Details Dialog */}
      <Dialog
        open={showParticipantDetailsDialog}
        onClose={() => {
          setShowParticipantDetailsDialog(false);
          setSelectedParticipantDetails(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Participant Yield Details
        </DialogTitle>
        <DialogContent>
          {selectedParticipantDetails && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedParticipantDetails.user_name}
            </Typography>
          )}
          {selectedParticipantDetails && selectedParticipantDetails.monthlyDetails ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Gross Yield:</strong> {dolorFormatAmount(selectedParticipantDetails.total_gross_yield || selectedParticipantDetails.gross_yield || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Commission:</strong> <span style={{ color: "#ff6b6b" }}>{dolorFormatAmount(selectedParticipantDetails.total_commission || 0)}</span>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Net Yield:</strong> <span style={{ color: "#00ba2d", fontWeight: "bold" }}>{dolorFormatAmount(selectedParticipantDetails.total_net_yield || selectedParticipantDetails.net_yield || 0)}</span>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Months Participated:</strong> {selectedParticipantDetails.months_participated || 0}
                </Typography>
              </Box>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Monthly Breakdown
              </Typography>
              {selectedParticipantDetails.monthlyDetails.map((monthDetail, monthIdx) => (
                <Box key={monthIdx} sx={{ mb: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    {dayjs().month(monthDetail.month - 1).format("MMMM")} {monthDetail.year}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Days</Typography>
                      <Typography variant="body2">{monthDetail.days_participated || 0}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Monthly %</Typography>
                      <Typography variant="body2">{monthDetail.monthly_percentage || 0}%</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Gross Yield</Typography>
                      <Typography variant="body2">{dolorFormatAmount(monthDetail.gross_yield || 0)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Net Yield</Typography>
                      <Typography variant="body2" sx={{ color: "#00ba2d", fontWeight: "bold" }}>
                        {dolorFormatAmount(monthDetail.net_yield || 0)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {monthDetail.dailyDetails && monthDetail.dailyDetails.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                        Day-by-Day Breakdown
                      </Typography>
                      <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Day</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell align="center">Active</TableCell>
                              <TableCell align="right">Balance</TableCell>
                              <TableCell align="right">Daily Yield</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {monthDetail.dailyDetails.map((detail) => (
                              <TableRow
                                key={detail.day}
                                sx={{
                                  backgroundColor: detail.isActive ? "rgba(0, 186, 45, 0.05)" : "transparent",
                                }}
                              >
                                <TableCell>{detail.day}</TableCell>
                                <TableCell>{dayjs(detail.date).format("DD/MM/YYYY")}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={detail.isActive ? "Yes" : "No"}
                                    size="small"
                                    color={detail.isActive ? "success" : "default"}
                                    sx={{ minWidth: 50 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {dolorFormatAmount(detail.balanceBeforeDeposit || 0)}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: detail.dailyYield > 0 ? "#00ba2d" : "text.secondary",
                                      fontWeight: detail.dailyYield > 0 ? "bold" : "normal",
                                    }}
                                  >
                                    {dolorFormatAmount(detail.dailyYield || 0)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Box>
              ))}
            </>
          ) : (
            <Alert severity="info">No monthly details available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowParticipantDetailsDialog(false);
              setSelectedParticipantDetails(null);
            }}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>⚠️ Delete Final Record</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete this record?
          </Typography>
          {recordToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography>
                <strong>Product:</strong> {recordToDelete.product_name}
              </Typography>
              <Typography>
                <strong>Transaction Date:</strong>{" "}
                {recordToDelete.transaction_date
                  ? dayjs(recordToDelete.transaction_date.toDate()).format("MMM DD, YYYY HH:mm")
                  : "N/A"}
              </Typography>
              <Typography>
                <strong>Participants:</strong> {recordToDelete.total_participants || recordToDelete.participants?.length || 0}
              </Typography>
              <Typography>
                <strong>Total Net Yield:</strong> {dolorFormatAmount(recordToDelete.total_net_yield || 0)}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              ⚠️ This reverts the ENTIRE final submission (full rollback).
            </Typography>
            <Typography variant="body2">
              For each participant it will: subtract the net yield from their balance,
              delete the created ledger entry and transaction, reset this product's
              yield_total_earned, and re-activate yield participation. The final record is
              then removed. Check the balances before/after to confirm.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeleteDialog(false);
              setRecordToDelete(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteRecord}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YieldFinalSubmission;
